export type FocusMetrics = {
  phoneMs: number;
  awayMs: number;
  tabAwayMs?: number; // Added for completeness
  hiddenTotalMs?: number; // Added for Pause logic
  notFocusedMs: number;
  cameraOffMs?: number;
  drowsyMs: number;
  postureMs: number;
  yawnPenaltyMs?: number; // Added for Yawn penalty
  sustainedFocusMs: number;
  totalFocusMs: number;
  elapsedMs?: number;
  totalSessionMs?: number;
};

export type FocusSample = {
  timestamp: number;
  isFocused: boolean;
  state: 'focused' | 'distracted';
  details?: string;
};

export type FocusHistory = FocusSample[];

export type FocusWeights = {
  phonePenaltyPerMin: number;
  awayPenaltyPerMin: number;
  notFocusedPenaltyPerMin: number;
  cameraOffPenaltyPerMin?: number;
  drowsyPenaltyPerMin: number;
  posturePenaltyPerMin: number;
  sustainedRewardPerBlock: number;
  blockSizeMs: number;
  maxScore: number;
  noBlockPenalty: number;
};

export const defaultWeights: FocusWeights = {
  phonePenaltyPerMin: 1,
  awayPenaltyPerMin: 1,
  notFocusedPenaltyPerMin: 1,
  cameraOffPenaltyPerMin: 1,
  drowsyPenaltyPerMin: 1,
  posturePenaltyPerMin: 0.5,
  sustainedRewardPerBlock: 0,
  blockSizeMs: 10 * 60 * 1000,
  maxScore: 100,
  noBlockPenalty: 0,
};

export const computeFocusScore = (m: FocusMetrics, w: FocusWeights, history?: FocusHistory) => {
  // New Logic based on SCORING_SYSTEM.md:
  // Score = ((Elapsed Time - Penalty Time) / Elapsed Time) * 100
  
  // 1. Elapsed Time
  const durationMs = m.elapsedMs && m.elapsedMs > 0 ? m.elapsedMs : (m.totalSessionMs || 1);
  
  // 2. Penalty Time: Sum of all distractions * weights
  const phoneMs = (m.phoneMs || 0) * (w.phonePenaltyPerMin || 1);
  const awayMs = (m.awayMs || 0) * (w.awayPenaltyPerMin || 1);
  const partialAttendanceMs = ((m.hiddenTotalMs || 0) + (m.tabAwayMs || 0)) * 1; // Always 1x for now
  const notFocusedMs = (m.notFocusedMs || 0) * (w.notFocusedPenaltyPerMin || 1);
  const cameraOffMs = (m.cameraOffMs || 0) * (w.cameraOffPenaltyPerMin || 1);
  const drowsyMs = (m.drowsyMs || 0) * (w.drowsyPenaltyPerMin || 1);
  const postureMs = (m.postureMs || 0) * (w.posturePenaltyPerMin || 1);
  const yawnPenaltyMs = m.yawnPenaltyMs || 0;
  
  // Total Penalty Calculation
  const totalPenaltyMs = phoneMs + awayMs + partialAttendanceMs + notFocusedMs + cameraOffMs + drowsyMs + postureMs + yawnPenaltyMs;
  
  // 3. Score Calculation
  // Score = (Duration - Penalty) / Duration
  
  const effectivePenaltyMs = Math.min(totalPenaltyMs, durationMs);
  const rawScore = ((durationMs - effectivePenaltyMs) / durationMs) * 100;
  
  // Clamp between 0 and 100
  const score = Math.max(0, Math.min(100, rawScore));

  // 4. Breakdown for UI
  const pointsLost = 100 - score;
  
  // Breakdown by category (approximate share of points lost)
  const breakdown: any = {};
  if (totalPenaltyMs > 0) {
    breakdown.phone = (phoneMs / totalPenaltyMs) * pointsLost;
    breakdown.away = (awayMs / totalPenaltyMs) * pointsLost;
    breakdown.confirmedPartial = (partialAttendanceMs / totalPenaltyMs) * pointsLost;
    breakdown.distracted = (notFocusedMs / totalPenaltyMs) * pointsLost;
    breakdown.camera_off = (cameraOffMs / totalPenaltyMs) * pointsLost;
    breakdown.drowsy = (drowsyMs / totalPenaltyMs) * pointsLost;
    breakdown.posture = (postureMs / totalPenaltyMs) * pointsLost;
    breakdown.yawn = (yawnPenaltyMs / totalPenaltyMs) * pointsLost;
  }

  return {
    score,
    penalties: pointsLost,
    rewards: 0,
    breakdown,
    blocks: Math.floor((m.sustainedFocusMs || 0) / (w.blockSizeMs || 600000))
  };
};
  // Focus Score is computed via time subtraction; per-minute penalties are not used.
  // We return pointsLost as 'penalties' for breakdown views.

export const computeAchievements = (m: FocusMetrics, score: number) => {
  const out: string[] = [];
  if (m.phoneMs <= 0) out.push('No Phone');
  if (m.sustainedFocusMs >= 15 * 60 * 1000) out.push('Sustained 15m');
  if (score >= 80) out.push('High Focus 80+');
  return out;
};
